import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, ArrowLeft, AlertCircle, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listSiteTimecards, correctTimePunch } from "@/lib/review.functions";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/review/timecards/$siteId")({
  component: SiteTimecardsPage,
});

type PunchType = "shift_start" | "shift_end" | "lunch_start" | "lunch_end" | "on_duty_meal";

function SiteTimecardsPage() {
  const { siteId } = Route.useParams();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const fetchCards = useServerFn(listSiteTimecards);
  const qc = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["site-timecards", orgId, siteId, from, to],
    queryFn: () =>
      fetchCards({
        data: { orgId: orgId!, siteId, from: from || undefined, to: to || undefined },
      }),
    enabled: !!orgId,
  });

  const [editing, setEditing] = useState<{ entryId: string; punchType: PunchType; breakId?: string } | null>(null);
  const [ts, setTs] = useState("");
  const [reason, setReason] = useState("");

  const correctFn = useServerFn(correctTimePunch);
  const correct = useMutation({
    mutationFn: () =>
      correctFn({
        data: {
          orgId: orgId!,
          entryId: editing!.entryId,
          punchType: editing!.punchType,
          breakId: editing!.breakId,
          newTimestamp: new Date(ts).toISOString(),
          reason,
        },
      }),
    onSuccess: () => {
      toast.success("Punch corrected — audit log written");
      setEditing(null);
      setTs("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["site-timecards", orgId, siteId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const guards = data?.guards ?? [];
  const filteredGuards = guards.filter((g) => {
    if (!search.trim()) return true;
    return g.guardName.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-4">
      <Link
        to="/review/timecards"
        className="inline-flex items-center gap-1 text-xs text-[#C0C0C0] hover:text-white"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to sites
      </Link>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <Field label="From">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="Search">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guards..."
              className="rounded-md border border-white/10 bg-black/40 pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-[#666]"
            />
          </div>
        </Field>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading timecards…
        </div>
      ) : filteredGuards.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
          No timecards at this site in the selected window.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredGuards.map((g) => (
            <div
              key={g.officerId}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
            >
              <div className="text-sm font-semibold text-white">{g.guardName}</div>
              <div className="mt-3 space-y-2">
                {g.entries.map((e) => {
                  const startIssue = false;
                  const endIssue = e.issues.includes("missing_clock_out") || e.issues.includes("negative_duration");
                  const breakIssue = e.issues.includes("missing_break_end");
                  return (
                    <div
                      key={e.id}
                      className="rounded-md border border-white/5 bg-black/30 px-3 py-2 text-xs"
                    >
                      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                        <PunchRow
                          label="Shift Start"
                          time={e.clockInAt}
                          issue={startIssue}
                          onCorrect={() => {
                            setEditing({ entryId: e.id, punchType: "shift_start" });
                            setTs(e.clockInAt.slice(0, 16));
                          }}
                        />
                        <PunchRow
                          label="Shift End"
                          time={e.clockOutAt}
                          issue={endIssue}
                          onCorrect={() => {
                            setEditing({ entryId: e.id, punchType: "shift_end" });
                            setTs((e.clockOutAt ?? new Date().toISOString()).slice(0, 16));
                          }}
                        />
                        {e.breaks.map((b) => (
                          <div key={b.id} className="contents">
                            <PunchRow
                              label={
                                b.break_type === "on_duty_meal"
                                  ? "On-Duty Meal Start"
                                  : b.break_type === "lunch"
                                    ? "Lunch Start"
                                    : "Break Start"
                              }
                              time={b.started_at}
                              issue={false}
                              onCorrect={() => {
                                setEditing({
                                  entryId: e.id,
                                  punchType:
                                    b.break_type === "on_duty_meal" ? "on_duty_meal" : "lunch_start",
                                  breakId: b.id,
                                });
                                setTs(b.started_at.slice(0, 16));
                              }}
                            />
                            <PunchRow
                              label={
                                b.break_type === "on_duty_meal"
                                  ? "On-Duty Meal End"
                                  : b.break_type === "lunch"
                                    ? "Lunch End"
                                    : "Break End"
                              }
                              time={b.ended_at}
                              issue={!b.ended_at && breakIssue}
                              onCorrect={() => {
                                setEditing({
                                  entryId: e.id,
                                  punchType: "lunch_end",
                                  breakId: b.id,
                                });
                                setTs((b.ended_at ?? new Date().toISOString()).slice(0, 16));
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-[10px] text-[#666]">
                        Worked {e.workedMinutes ?? 0}m · Breaks {e.breakMinutes}m · {e.status}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual punch correction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Punch type: <span className="font-semibold">{editing?.punchType}</span>
            </div>
            <label className="block space-y-1">
              <span className="text-xs">Corrected timestamp</span>
              <Input type="datetime-local" value={ts} onChange={(e) => setTs(e.target.value)} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs">Reason (required, audited)</span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="e.g. Guard forgot to clock out, verified with supervisor"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              disabled={!ts || reason.length < 3 || correct.isPending}
              onClick={() => correct.mutate()}
            >
              {correct.isPending ? "Saving…" : "Save correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PunchRow({
  label,
  time,
  issue,
  onCorrect,
}: {
  label: string;
  time: string | null;
  issue: boolean;
  onCorrect: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded bg-white/[0.02] px-2 py-1">
      <div className="flex items-center gap-2">
        {issue && (
          <button
            type="button"
            onClick={onCorrect}
            aria-label={`Correct ${label}`}
            className="text-[#CC0000] hover:text-[#FF4444]"
          >
            <AlertCircle className="h-4 w-4" />
          </button>
        )}
        <span className="text-[#C0C0C0]">{label}</span>
      </div>
      <button
        type="button"
        onClick={onCorrect}
        className="font-mono text-white hover:underline"
      >
        {time ? new Date(time).toLocaleString() : "—"}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-[#C0C0C0]">
      <span>{label}</span>
      {children}
    </label>
  );
}
